import React, { useEffect, useState } from "react";
import { MapContainer, TileLayer, Marker, Polygon, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { toast } from "sonner";
import { Loader2, MapPin, Search, X } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { Input } from "@/components/ui/input";
import { fieldCls } from "@/components/signup/SignupFields";
import { inNorway, loadNorway, toOutline } from "@/lib/norway";

const NORWAY = [[57.9, 4.6], [71.2, 31.1]];
const icon = (n) => L.divIcon({ className: "", html: `<div class="tfj-signup-pin"><span>${n}</span></div>`, iconSize: [34, 42], iconAnchor: [17, 42] });

const ClickToAdd = ({ onAdd }) => {
  useMapEvents({ click: (e) => onAdd(e.latlng) });
  return null;
};

const FlyTo = ({ target }) => {
  const map = useMap();
  useEffect(() => {
    if (target) map.flyTo(target, 16, { duration: 0.8 });
  }, [target, map]);
  return null;
};

const PlaceSearch = ({ onPick }) => {
  const { t, lang } = useLang();
  const s = t.pages.signup;
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    const term = q.trim();
    if (term.length < 2 || busy) return;
    setBusy(true);
    try {
      const r = await fetch(`https://nominatim.openstreetmap.org/search?format=jsonv2&countrycodes=no&limit=5&accept-language=${lang}&q=${encodeURIComponent(term)}`);
      setRes(r.ok ? await r.json() : []);
    } catch {
      setRes([]);
    }
    setBusy(false);
  };
  const pick = (r) => {
    onPick([Number(r.lat), Number(r.lon)]);
    setRes(null);
  };
  return (
    <div className="relative">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-muted" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                run();
              }
            }}
            placeholder={s.search}
            aria-label={s.search}
            data-testid="signup-map-search"
            className={`${fieldCls} pl-9`}
          />
        </div>
        <button type="button" onClick={run} data-testid="signup-map-search-btn" className="rounded-xl bg-ink hover:bg-[#1f1c18] text-parchment px-4 font-extrabold inline-flex items-center gap-2 transition-colors">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} {s.searchBtn}
        </button>
      </div>
      {res && (
        <ul data-testid="signup-map-results" className="absolute z-[1000] mt-2 w-full rounded-xl border border-line bg-parchment shadow-journal-lg overflow-hidden">
          {res.length ? (
            res.map((r, i) => (
              <li key={r.place_id}>
                <button type="button" onClick={() => pick(r)} data-testid={`signup-map-result-${i}`} className="w-full text-left px-4 py-2.5 text-sm text-ink hover:bg-moss/10">
                  {r.display_name}
                </button>
              </li>
            ))
          ) : (
            <li className="px-4 py-3 text-sm text-ink-muted">{s.noResults}</li>
          )}
        </ul>
      )}
    </div>
  );
};

const PinList = ({ pins, onRemove, host }) => {
  const { t } = useLang();
  const s = t.pages.signup;
  return (
    <ul className="grid sm:grid-cols-2 gap-2" data-testid="signup-pin-list">
      {pins.map((p, i) => (
        <li key={p.id} className="flex items-center justify-between gap-2 rounded-xl border border-line bg-parchment px-3 py-2 text-sm">
          <span className="inline-flex items-center gap-2 font-bold text-ink">
            <MapPin className="w-4 h-4 text-rowan" /> {host ? s.placePin : s.pin.replace("{n}", i + 1)}
            <span className="font-semibold text-ink-muted tabular-nums">{p.lat.toFixed(4)}, {p.lng.toFixed(4)}</span>
          </span>
          <button type="button" onClick={() => onRemove(p.id)} aria-label={s.remove} data-testid={`signup-pin-remove-${i}`} className="p-1 rounded-full text-rowan hover:bg-rowan/10">
            <X className="w-4 h-4" />
          </button>
        </li>
      ))}
    </ul>
  );
};

export const PinMap = ({ pins, setPins, max, host }) => {
  const { t } = useLang();
  const s = t.pages.signup;
  const [target, setTarget] = useState(null);
  const [outline, setOutline] = useState(null);
  useEffect(() => {
    loadNorway().then((d) => setOutline(toOutline(d)));
  }, []);
  const add = (ll) => {
    if (!max) return toast(s.noPackage);
    if (!inNorway(ll)) return toast(s.outside);
    if (pins.length >= max) return toast(host ? s.fullHost : s.full);
    setPins((ps) => [...ps, { id: `${Date.now()}-${ps.length}`, lat: ll.lat, lng: ll.lng }]);
  };
  const move = (p, marker) => {
    const ll = marker.getLatLng();
    if (!inNorway(ll)) {
      toast(s.outside);
      marker.setLatLng([p.lat, p.lng]);
      return;
    }
    setPins((ps) => ps.map((x) => (x.id === p.id ? { ...x, lat: ll.lat, lng: ll.lng } : x)));
  };
  return (
    <div data-testid="signup-map-block" className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h3 data-testid="signup-map-title" className="font-display text-xl font-semibold text-ink">{host ? s.mapTitleHost : s.mapTitle} *</h3>
          <p className="text-sm text-ink-muted mt-1">{host ? s.mapHintHost : s.mapHint}</p>
        </div>
        <span data-testid="signup-pin-counter" className="rounded-full bg-moss/10 text-moss px-3 py-1 text-sm font-extrabold tabular-nums">
          {max ? (host ? s.placedHost : s.placed).replace("{n}", pins.length).replace("{max}", max) : s.noPackage}
        </span>
      </div>
      <PlaceSearch onPick={setTarget} />
      <div className="relative h-[380px] md:h-[460px] rounded-[20px] overflow-hidden border border-line shadow-journal" data-testid="signup-map">
        <MapContainer bounds={NORWAY} scrollWheelZoom={false} className="w-full h-full tfj-map">
          <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors' url="https://tile.openstreetmap.org/{z}/{x}/{y}.png" />
          <ClickToAdd onAdd={add} />
          <FlyTo target={target} />
          {outline && <Polygon positions={outline} interactive={false} pathOptions={{ color: "#6D7C5E", weight: 2, fillColor: "#6D7C5E", fillOpacity: 0.08 }} />}
          {pins.map((p, i) => (
            <Marker key={p.id} position={[p.lat, p.lng]} icon={icon(i + 1)} draggable eventHandlers={{ dragend: (e) => move(p, e.target) }} />
          ))}
        </MapContainer>
      </div>
      {pins.length > 0 && <PinList host={host} pins={pins} onRemove={(id) => setPins((ps) => ps.filter((p) => p.id !== id))} />}
    </div>
  );
};
